import angular from 'angular';
import _ from 'lodash';
import { EMPLOYER_WORKFLOW_STATUS } from '../constants/employer-workflow';
import { CANDIDATE_FEEDBACK_STATUSES } from '../../recruiter/constants/feedback';
import Logger from '../../../shared/logger';

const PositionCache = {};

const EmployerFeedbackService = (
    CandidateService,
    FeedbackFilterService,
    PositionService,
    Restangular,
    CommonCodeService,
) => {
    const getPreparedCandidate = (candidate, talent) => {
        return {
            ...candidate,
            fullName: talent.name,
            talentID: talent.talentId,
            currentJob: _.find(talent.employmentHistory, { isCurrent: true }),
            previousJob: _.find(talent.employmentHistory, { isCurrent: false }),
            predictionScore: talent.predictionScore,
            residency: CommonCodeService.getLocationString(talent.residency),
            candidateData: {
                ...talent,
                matchScore: candidate.matchScore,
            },
        };
    };

    const getFeedbackCandidates = async ({
        employerId,
        positionId,
        isListView,
        pageOptions,
        filter,
    }) => {
        const { result: {
            list: feedbackCandidates,
            total,
            counters,
            talentIds,
        } } = await Restangular
            .one('employer', employerId)
            .one('position', positionId)
            .one('workflowStep', EMPLOYER_WORKFLOW_STATUS.FEEDBACK)
            .all('candidates')
            .post({
                isListView,
                pageOptions,
                filter,
            });

        const list = (feedbackCandidates || []).map(candidate => getPreparedCandidate(candidate, candidate));

        return {
            list,
            total,
            counters,
            talentIds,
        };
    };

    const getFeedbackSamples = async (positionId) => {
        const manuallyAddedSamples = await PositionService.getSelectedAndFeedbackAndManuallyAddedSamples(positionId, {
            populateData: true,
            exposeDetails: false,
        });

        return manuallyAddedSamples.map(sampleData => getPreparedCandidate({
            ...sampleData.candidateData,
            sample: { ...sampleData, candidateData: undefined },
        }, sampleData.candidateData));
    };

    const getAutoGeneratedFeedbackCandidates = async (positionId) => {
        const { result: generatedCandidates } = await Restangular
            .one('position', positionId)
            .all('wizard')
            .all('generateFeedback')
            .all('0')
            .post({ limit: 20 })
            .catch(error => Logger.error(error));

        return (generatedCandidates || []).map(candidate => getPreparedCandidate({
            ...candidate,
            feedback: { status: CANDIDATE_FEEDBACK_STATUSES.PENDING },
        }, candidate));
    };

    const initAutoFeedbackGenerator = async ({ positionId }) => {
        return Restangular
            .one('position', positionId)
            .all('wizard')
            .all('generateFeedback')
            .all('0')
            .post({})
            .catch(error => Logger.error(error));
    };

    const populateCandidateWithFeedbackProvider = async (positionId, candidates) => {
        let positionData = PositionCache[positionId];

        if (!positionData) {
            const { result } = await PositionService.getPosition(positionId);

            PositionCache[positionId] = result;

            positionData = result;
        }

        const feedbackProviders = await PositionService.getFeedbackProviders(positionData);

        return (candidates || []).map(candidate => CandidateService.extendFeedbackData(candidate, feedbackProviders));
    };

    const getFeedbackWorkflowCounters = ({ feedbackOnCandidates, generatedFeedbackTalents } = {}) => {
        const all = feedbackOnCandidates.length;
        const rated = _.countBy(feedbackOnCandidates, feedback => feedback.status === CANDIDATE_FEEDBACK_STATUSES.ACCEPTED).true || 0;
        const nonRated = _.countBy(feedbackOnCandidates, feedback => feedback.status === CANDIDATE_FEEDBACK_STATUSES.PENDING).true || 0;
        const resultNonRated = nonRated + (generatedFeedbackTalents || []).length;

        return {
            all,
            rated,
            nonRated: resultNonRated,
        };
    };

    const createFeedback = async (position, feedback) =>
        PositionService.putPositionFeedback(position._id, feedback);

    const updateFeedback = async (position, feedback) =>
        PositionService.putPositionFeedback(position._id, feedback);

    const getFeedbackCounters = async ({ employerId, positionId, runGenerator }) => {
        const { result: counters } = await Restangular
            .one('employer', employerId)
            .one('position', positionId)
            .all('feedbackCounters')
            .post({ runGenerator });

        return counters;
    };

    return {
        createFeedback,
        getFeedbackCandidates,
        getFeedbackCounters,
        getFeedbackSamples,
        getAutoGeneratedFeedbackCandidates,
        getFeedbackWorkflowCounters,
        initAutoFeedbackGenerator,
        populateCandidateWithFeedbackProvider,
        updateFeedback,
        getPreparedCandidate,
    };
};

angular
    .module('TALENYA-FE')
    .factory('EmployerFeedbackService', EmployerFeedbackService);
